/**
 * NAIVASTRA Luxury Wishlist System (Client-side localStorage Engine)
 * Engineered for Online Store 2.0 & Bluorng Luxury Aesthetics
 * - Real-time header count badge
 * - Cross-page persistent storage (localStorage)
 * - Dynamic Wishlist Page renderer (/pages/wishlist)
 * - Real-time synchronization across all product cards & PDP
 * - Direct Move to Bag integration
 */
(function() {
  const STORAGE_KEY = 'naivastra_wishlist';

  const NaivastraWishlist = {
    get() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        console.error('Error reading wishlist', e);
        return [];
      }
    },

    save(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.error('Error saving wishlist', e);
      }
      this.updateBadges();
      this.syncButtons();
      this.renderPage();
      window.dispatchEvent(new CustomEvent('naivastra:wishlist:updated', { detail: { items } }));
    },

    has(id) {
      if (!id) return false;
      const strId = String(id);
      return this.get().some(item => String(item.id) === strId);
    },

    add(item) {
      if (!item || !item.id) return;
      const items = this.get();
      const strId = String(item.id);
      if (!items.some(i => String(i.id) === strId)) {
        items.unshift({
          id: strId,
          title: item.title || 'Luxury Garment',
          price: item.price || '',
          compare_at_price: item.compare_at_price || '',
          image: item.image || '',
          url: item.url || '#',
          variant_id: item.variant_id || item.variantId || ''
        });
        this.save(items);
        this.showToast('Saved to Wishlist');
      }
    },

    remove(id) {
      if (!id) return;
      const strId = String(id);
      const items = this.get().filter(item => String(item.id) !== strId);
      this.save(items);
      this.showToast('Removed from Wishlist');
    },

    toggle(item) {
      if (!item || !item.id) return;
      if (this.has(item.id)) {
        this.remove(item.id);
      } else {
        this.add(item);
      }
    },

    clear() {
      if (confirm('Are you sure you want to clear your wishlist?')) {
        this.save([]);
        this.showToast('Wishlist cleared');
      }
    },

    count() {
      return this.get().length;
    },

    updateBadges() {
      const count = this.count();
      // Update header badges
      const headerBadges = document.querySelectorAll('.naivastra-header-wishlist-count');
      headerBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'inline-flex' : 'none';
      });

      // Update mobile drawer count
      const drawerBadges = document.querySelectorAll('.naivastra-drawer-wishlist-count');
      drawerBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'inline-block' : 'none';
      });

      // Update wishlist page count
      const pageCountEl = document.getElementById('NaivastraWishlistCount');
      if (pageCountEl) {
        pageCountEl.textContent = `${count} ${count === 1 ? 'ITEM' : 'ITEMS'}`;
      }

      const pageSubtitleCount = document.getElementById('NaivastraWishlistSubtitleCount');
      if (pageSubtitleCount) {
        pageSubtitleCount.textContent = count;
      }
    },

    syncButtons() {
      const items = this.get();
      const ids = new Set(items.map(i => String(i.id)));

      document.querySelectorAll('.naivastra-card__wishlist-btn, .naivastra-pdp-bookmark-btn, .naivastra-pdp-wishlist-btn').forEach(btn => {
        const id = String(btn.dataset.productId || '');
        if (id && ids.has(id)) {
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    },

    showToast(message) {
      let toast = document.getElementById('naivastraWishlistToast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'naivastraWishlistToast';
        toast.className = 'naivastra-wishlist-toast';
        document.body.appendChild(toast);
      }
      toast.innerHTML = `
        <div class="naivastra-wishlist-toast-inner">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
          <span>${message}</span>
        </div>
      `;
      toast.classList.add('is-visible');
      clearTimeout(toast._timer);
      toast._timer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 2600);
    },

    async moveToBag(id, variantId, url, btn) {
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'ADDING...';
      }

      if (variantId && variantId !== 'undefined' && variantId !== '') {
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 1 })
          });
          if (res.ok) {
            // Update cart badges
            const cartRes = await fetch('/cart.js');
            if (cartRes.ok) {
              const cartData = await cartRes.json();
              document.querySelectorAll('.naivastra-header-cart-count').forEach(el => {
                el.textContent = cartData.item_count;
                el.style.display = cartData.item_count > 0 ? 'inline-flex' : 'none';
              });
            }
            this.remove(id);
            this.showToast('Item moved to bag!');
            return;
          }
        } catch (e) {
          console.error('Error adding to cart', e);
        }
      }

      // Fallback: navigate to PDP if size selection is needed
      if (url && url !== '#') {
        window.location.href = url;
      } else {
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'VIEW PRODUCT';
        }
      }
    },

    renderPage() {
      const grid = document.getElementById('NaivastraWishlistGrid');
      const emptyState = document.getElementById('NaivastraWishlistEmpty');
      const contentSection = document.getElementById('NaivastraWishlistContent');
      if (!grid) return;

      const items = this.get();

      if (items.length === 0) {
        if (contentSection) contentSection.style.display = 'none';
        if (emptyState) emptyState.style.display = 'flex';
        grid.innerHTML = '';
        return;
      }

      if (emptyState) emptyState.style.display = 'none';
      if (contentSection) contentSection.style.display = 'block';

      grid.innerHTML = items.map(item => {
        const hasSale = Boolean(item.compare_at_price && item.compare_at_price !== item.price);
        return `
          <div class="naivastra-card naivastra-wishlist-card" id="WishlistItem-${item.id}">
            <div class="naivastra-card__media-container">
              ${hasSale ? '<span class="naivastra-card__badge">SALE</span>' : ''}
              
              <button
                type="button"
                class="naivastra-card__wishlist-btn active"
                aria-label="Remove from Wishlist"
                title="Remove from Wishlist"
                onclick="event.stopPropagation(); NaivastraWishlist.remove('${item.id}')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                </svg>
              </button>

              <a href="${item.url || '#'}" class="naivastra-card__media-link">
                <div class="naivastra-card__slides">
                  <img
                    src="${item.image || 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=800&q=80'}"
                    alt="${item.title}"
                    class="naivastra-card__img"
                    loading="lazy"
                  >
                </div>
              </a>
            </div>

            <div class="naivastra-card__info">
              <div class="naivastra-card__details">
                <p class="naivastra-card__vendor">NAIVASTRA</p>
                <a href="${item.url || '#'}" class="naivastra-card__title">${item.title}</a>
                <div class="naivastra-card__price-row">
                  <span class="naivastra-card__price">${item.price || 'RS. 4,990.00'}</span>
                  ${hasSale ? `<span class="naivastra-card__price-compare">${item.compare_at_price}</span>` : ''}
                </div>
              </div>

              <button
                type="button"
                class="naivastra-wishlist-card-btn"
                onclick="event.stopPropagation(); NaivastraWishlist.moveToBag('${item.id}', '${item.variant_id || ''}', '${item.url}', this)"
              >
                ${item.variant_id ? 'MOVE TO BAG' : 'VIEW PRODUCT'}
              </button>
            </div>
          </div>
        `;
      }).join('');
    }
  };

  window.NaivastraWishlist = NaivastraWishlist;

  // Global helper for card & PDP buttons
  window.toggleNAIVASTRAWishlist = function(btn) {
    if (!btn) return;
    let id = btn.dataset.productId;
    let title = btn.dataset.productTitle;
    let price = btn.dataset.productPrice;
    let comparePrice = btn.dataset.productComparePrice;
    let img = btn.dataset.productImg;
    let url = btn.dataset.productUrl;
    let variantId = btn.dataset.productVariantId;

    // Fallback extraction from card context if dataset missing
    if (!id) {
      const card = btn.closest('.naivastra-card');
      if (card) {
        id = card.id ? card.id.replace('NAIVASTRACard-', '') : ('prod_' + Math.random().toString(36).substr(2, 9));
        const titleEl = card.querySelector('.naivastra-card__title');
        if (titleEl) {
          title = titleEl.textContent.trim();
          url = titleEl.getAttribute('href');
        }
        const priceEl = card.querySelector('.naivastra-card__price');
        if (priceEl) price = priceEl.textContent.trim();
        const imgEl = card.querySelector('.naivastra-card__img');
        if (imgEl) img = imgEl.src;
        const quickAddBtn = card.querySelector('.naivastra-card__quick-add-trigger');
        if (quickAddBtn && quickAddBtn.dataset.variantId) {
          variantId = quickAddBtn.dataset.variantId;
        }
      }
    }

    if (id) {
      NaivastraWishlist.toggle({
        id,
        title: title || 'Luxury Garment',
        price: price || '',
        compare_at_price: comparePrice || '',
        image: img || '',
        url: url || '#',
        variant_id: variantId || ''
      });
    }
  };

  // Initialize
  function init() {
    NaivastraWishlist.updateBadges();
    NaivastraWishlist.syncButtons();
    NaivastraWishlist.renderPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle Shopify theme editor & navigation
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', init);
})();
